import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar, Clock, MessageCircle, Phone, Edit, CheckCircle, HelpCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getSessionBookings, SessionBookingWithMentee, updateSessionBookingStatus } from '@/utils/sessionBookingsUtils';
import { format, parseISO, isAfter, startOfDay } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useDailyVideoCall } from '@/hooks/useDailyVideoCall';
import { useCallDurationManager } from '@/hooks/useCallDurationManager';
import { DailyCallManager } from '@/components/DailyCallManager';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { decrementBookingsUsed, dispatchSubscriptionBookingUpdate } from '@/utils/subscriptionBookingUtils';
import { supabase } from '@/integrations/supabase/client';
import { getUserTimezone, convertFromUTC, getTimezoneAbbreviation } from '@/utils/timezoneUtils';

// Normalize course type string to a consistent label
function normalizeCourseType(type?: string | null): string | null {
  if (!type) return null;
  const t = String(type).trim().toLowerCase();
  if (t.includes('subscription')) return 'Subscription';
  if (['voicecall','voice call','video call','call','voice/video call','voice / video call'].includes(t)) return 'Voice/Video Call';
  return type;
}

const UpcomingAppointments = () => {
  const { user } = useAuth();
  const { findOrCreateVideoRoom, startVideoCall, isCreatingRoom } = useDailyVideoCall();
  const { isCallActive, callDuration, startCallTimer, endCallTimer } = useCallDurationManager();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<SessionBookingWithMentee[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseTypes, setCourseTypes] = useState<{[key: string]: string | null}>({});
  const [callInProgress, setCallInProgress] = useState<{[key: string]: boolean}>({});
  const [isCompleting, setIsCompleting] = useState<{[key: string]: boolean}>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userTimezone] = useState<string>(getUserTimezone());

  useEffect(() => {
    if (user) {
      loadUpcomingBookings();
    }
  }, [user]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const loadUpcomingBookings = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // First, update any bookings that should be completed
      await updateCompletedBookings();
      
      const result = await getSessionBookings(user.id);
      if (result.success && result.data) {
        // Filter for upcoming bookings with subscription status check
        const upcomingBookings = result.data.filter(booking => {
          const bookingDate = parseISO(booking.booked_date);
          const today = startOfDay(new Date());
          
          // Existing date and status checks
          const isUpcoming = (isAfter(bookingDate, today) || format(bookingDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) 
                             && (booking.status === 'confirmed' || booking.status === 'pending');
          
          // New subscription status check
          const isValidSubscription = booking.course_type !== 'subscription' || 
            (booking.is_subscription_active && 
             (!booking.subscription_end_date || new Date(booking.subscription_end_date) > new Date()));
          
          console.log('DEBUG: Filtering mentor booking', {
            bookingId: booking.id,
            courseType: booking.course_type,
            isSubscriptionActive: booking.is_subscription_active,
            subscriptionEndDate: booking.subscription_end_date,
            isUpcoming,
            isValidSubscription
          });
          
          return isUpcoming && isValidSubscription;
        }).sort((a, b) => new Date(a.booked_date).getTime() - new Date(b.booked_date).getTime());
        
        setBookings(upcomingBookings);
        
        // Initialize course types for each booking
        const types: {[key: string]: string | null} = {};
        upcomingBookings.forEach(booking => {
          types[booking.id] = normalizeCourseType(booking.course_type);
        });
        setCourseTypes(types);
        
        // Fetch missing course types
        upcomingBookings.forEach(booking => {
          if (!types[booking.id] && booking.application_id) {
            fetchCourseType(booking.id, booking.application_id);
          }
        });
      }
    } catch (error) {
      console.error('Error loading upcoming bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCompletedBookings = async () => {
    try {
      const { error } = await supabase.rpc('update_voicevideo_bookings_to_completed');
      if (error) {
        console.error('Error updating completed bookings:', error);
      }
    } catch (error) {
      console.error('Error calling update function:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCourseTypeDisplay = (courseType: string, subscriptionType?: string) => {
    if (courseType === 'subscription') {
      return subscriptionType === 'monthly' ? 'Monthly Mentoring' : 'Weekly Mentoring';
    }
    return courseType || 'Voice/Video Call';
  };

  const getCourseTypeColor = (courseType: string) => {
    switch (courseType) {
      case 'subscription': return 'bg-blue-100 text-blue-800';
      case 'Voice/Video Call': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const fetchCourseType = async (bookingId: string, applicationId: string) => {
    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          courses (
            course_type
          )
        `)
        .eq('id', applicationId)
        .single();

      if (error) {
        console.error('Error fetching course type:', error);
        return;
      }

      if (data?.courses) {
        const normalized = normalizeCourseType(data.courses.course_type);
        setCourseTypes(prev => ({
          ...prev,
          [bookingId]: normalized
        }));
      }
    } catch (error) {
      console.error('Error in fetchCourseType:', error);
    }
  };

  const handleBookingUpdated = (bookingId: string, newStatus: string) => {
    // Update the local state
    setBookings(prev => prev.filter(booking => booking.id !== bookingId));
    
    // Reload the bookings to ensure we have the latest data
    loadUpcomingBookings();
  };

  // Helper function to determine if message button should be shown
  const shouldShowMessageButton = (bookingId: string) => {
    return courseTypes[bookingId] === 'Subscription';
  };

  const handleMessageMentee = async (booking: SessionBookingWithMentee) => {
    if (!user) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to send messages.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if conversation already exists
      const { data: existingConversation, error: fetchError } = await supabase
        .from('conversations')
        .select('id')
        .eq('mentor_id', user.id)
        .eq('mentee_id', booking.mentee_id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error checking existing conversation:', fetchError);
        toast({
          title: "Error",
          description: "Failed to check existing conversation. Please try again.",
          variant: "destructive",
        });
        return;
      }

      let conversationId = existingConversation?.id;

      // If no conversation exists, create one
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            mentor_id: user.id,
            mentee_id: booking.mentee_id,
          })
          .select('id')
          .single();

        if (createError) {
          console.error('Error creating conversation:', createError);
          toast({
            title: "Error",
            description: "Failed to create conversation. Please try again.",
            variant: "destructive",
          });
          return;
        }

        conversationId = newConversation.id;
      }

      // Navigate to the conversation
      navigate(`/messages/${conversationId}`);
    } catch (error) {
      console.error('Error in handleMessageMentee:', error);
      toast({
        title: "Error",
        description: "Failed to start conversation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStartCall = async (booking: SessionBookingWithMentee) => {
    try {
      setCallInProgress(prev => ({ ...prev, [booking.id]: true }));
      
      console.log('Starting video call for booking:', booking.id);
      
      // Find or create Daily.co room using the new shared room logic
      const sessionData = await findOrCreateVideoRoom({
        mentorId: booking.mentor_id,
        menteeId: booking.mentee_id,
        bookingId: booking.id
      });

      if (sessionData) {
        // Start the call duration timer
        await startCallTimer(booking.mentor_id, booking.mentee_id, booking.booked_date);
        
        // Open the video call
        startVideoCall(sessionData.room_url, () => {
          console.log('Call started, duration tracking active');
        });
        
        toast({
          title: "Video Call Started",
          description: "The video call has been opened in a new window.",
        });
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      toast({
        title: "Error",
        description: "Failed to start video call. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCallInProgress(prev => ({ ...prev, [booking.id]: false }));
    }
  };

  const handleMarkAsComplete = async (booking: SessionBookingWithMentee) => {
    setIsCompleting(prev => ({ ...prev, [booking.id]: true }));
    try {
      const result = await updateSessionBookingStatus(booking.id, 'completed');
      
      if (result.success) {
        toast({
          title: "Appointment Completed",
          description: "The appointment has been marked as complete. Reviews are now available.",
        });
        
        // Notify parent component of the status change
        handleBookingUpdated(booking.id, 'completed');
        
        // Dispatch event to notify review components to refresh
        window.dispatchEvent(new CustomEvent('session-marked-complete', {
          detail: { 
            bookingId: booking.id, 
            mentorId: booking.mentor_id, 
            menteeId: booking.mentee_id,
            applicationId: booking.application_id
          }
        }));
      } else {
        toast({
          title: "Error",
          description: "Failed to mark appointment as complete. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error marking appointment as complete:', error);
      toast({
        title: "Error",
        description: "Failed to mark appointment as complete. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCompleting(prev => ({ ...prev, [booking.id]: false }));
      setShowConfirmDialog(null);
    }
  };

  const handleRequestChange = async (booking: SessionBookingWithMentee) => {
    try {
      console.log('DEBUG: Request Change started for booking:', booking.id, 'application_id:', booking.application_id);
      
      // 1) Cancel this booking
      const result = await updateSessionBookingStatus(booking.id, 'cancelled');
      if (!result.success) throw new Error('Failed to update booking status');

      // 2) For non-subscription: revert application to bookingRequired
      //    For subscription: give the slot back by decrementing bookings_used for the correct week
      if (booking.application_id) {
        // Check course type directly from database instead of relying on local state
        const { data: application, error: appError } = await supabase
          .from('applications')
          .select(`
            id,
            courses!applications_course_id_fkey(course_type)
          `)
          .eq('id', booking.application_id)
          .single();

        if (appError) {
          console.error('DEBUG: Error fetching application details:', appError);
          throw new Error('Failed to fetch application details');
        }

        const courseType = application?.courses?.course_type;
        console.log('DEBUG: Course type for application:', courseType);

        if (courseType === 'subscription') {
          console.log('DEBUG: Processing subscription booking - subscription_booking_id:', booking.subscription_booking_id);
          
          // Only try to decrement if we have a subscription booking ID
          if (booking.subscription_booking_id) {
            const dec = await decrementBookingsUsed(
              booking.application_id, 
              booking.subscription_booking_id, 
              booking.booked_date
            );
            if (!dec.success) {
              console.warn('DEBUG: decrementBookingsUsed failed, but continuing:', dec.error);
            } else {
              console.log('DEBUG: Successfully decremented subscription slot');
            }
          } else {
            console.warn('DEBUG: Subscription booking but no subscription_booking_id - skipping decrement');
          }
          
          // Notify lists to refresh subscription cards
          dispatchSubscriptionBookingUpdate(booking.application_id, booking.mentor_id, booking.mentee_id);
        } else {
          console.log('DEBUG: Processing non-subscription booking - updating application status');
          const { error: updateError } = await supabase
            .from('applications')
            .update({ status: 'bookingRequired', updated_at: new Date().toISOString() })
            .eq('id', booking.application_id);
          
          if (updateError) {
            console.error('DEBUG: Error updating application status:', updateError);
            throw updateError;
          }
        }
      } else {
        console.log('DEBUG: No application_id found for booking - skipping application updates');
      }

      // 3) Fetch mentor's profile to get their name for the notification
      const { data: mentorProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", booking.mentor_id)
        .single();

      // Construct display name with fallback
      const mentorDisplayName = mentorProfile?.first_name && mentorProfile?.last_name 
        ? `${mentorProfile.first_name} ${mentorProfile.last_name}`.trim()
        : "Your mentor";

      // 4) Notify the mentee
      const { error: notifError } = await supabase.from('notifications').insert({
        receiver_id: booking.mentee_id,
        type: 'booking_change_requested',
        content: `${mentorDisplayName} requested a change to your session. Please re-book a new time.`,
        link: '/mentee-dashboard'
      });
      if (notifError) {
        console.warn('Failed to create notification:', notifError);
      }

      // 4) Let parent update local state
      handleBookingUpdated(booking.id, 'cancelled');

      // 5) Dispatch a global event so lists can refresh
      window.dispatchEvent(new CustomEvent('booking-change-requested', {
        detail: { bookingId: booking.id, applicationId: booking.application_id, mentorId: booking.mentor_id, menteeId: booking.mentee_id }
      }));

      toast({
        title: 'Change requested',
        description: 'The mentee has been notified to re-book this session.',
      });
    } catch (error) {
      console.error('Error requesting change:', error);
      toast({
        title: 'Error',
        description: 'Could not request a change. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleConfirm = async (booking: SessionBookingWithMentee) => {
    try {
      const result = await updateSessionBookingStatus(booking.id, 'confirmed');
      if (!result.success) throw new Error('Failed to confirm booking');

      handleBookingUpdated(booking.id, 'confirmed');
      window.dispatchEvent(new CustomEvent('booking-status-updated', {
        detail: { bookingId: booking.id, status: 'confirmed' }
      }));

      toast({ title: 'Booking confirmed', description: 'This session has been confirmed.' });
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast({ title: 'Error', description: 'Failed to confirm booking.', variant: 'destructive' });
    }
  };

  // Check if Mark as Complete button should be shown - only after session end time
  const shouldShowMarkComplete = (booking: SessionBookingWithMentee) => {
    const courseType = courseTypes[booking.id];
    
    // Basic conditions check
    if (!(booking.status === 'confirmed' && !!booking.application_id && courseType === 'Voice/Video Call')) {
      return false;
    }
    
    // Time-based check - only show after session end time
    try {
      // Convert UTC end time to user's local timezone
      const { localDate: endTimeLocal } = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );
      
      const now = currentTime;
      
      // Only show the button after the session has ended
      return now > endTimeLocal;
    } catch (error) {
      console.error('Error parsing appointment times for Mark Complete:', error);
      return false;
    }
  };

  // Check if Request Change button should be shown - hide after session end time
  const shouldShowRequestChange = (booking: SessionBookingWithMentee) => {
    // Time-based check - hide after session end time
    try {
      // Convert UTC end time to user's local timezone
      const { localDate: endTimeLocal } = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );
      
      const now = currentTime;
      
      // Only show the button BEFORE the session has ended
      return now <= endTimeLocal;
    } catch (error) {
      console.error('Error parsing appointment times for Request Change:', error);
      // On error, default to showing the button
      return true;
    }
  };

  // Check if action buttons should be shown (hidden when completed)
  const shouldShowActionButtons = (booking: SessionBookingWithMentee) => {
    return booking.status !== 'completed';
  };

  // Check if call button should be enabled based on time
  const getCallButtonState = (booking: SessionBookingWithMentee) => {
    if (booking.status !== 'confirmed') {
      return { enabled: false, tooltip: 'Appointment must be confirmed to start call' };
    }

    try {
      // Convert UTC start time to user's local timezone
      const { localDate: startTimeLocal, localTime: startTimeFormatted } = convertFromUTC(
        booking.booked_date,
        booking.start_time,
        userTimezone
      );
      
      // Convert UTC end time to user's local timezone
      const { localDate: endTimeLocal, localTime: endTimeFormatted } = convertFromUTC(
        booking.booked_date,
        booking.end_time,
        userTimezone
      );
      
      const now = currentTime;
      
      if (now < startTimeLocal) {
        return { 
          enabled: false, 
          tooltip: `The call will be available at ${startTimeFormatted}` 
        };
      } else if (now > endTimeLocal) {
        return { 
          enabled: false, 
          tooltip: `This call ended at ${endTimeFormatted}` 
        };
      } else {
        return { 
          enabled: true, 
          tooltip: 'Click to start your scheduled call' 
        };
      }
    } catch (error) {
      console.error('Error parsing appointment times:', error);
      return { enabled: false, tooltip: 'Error loading appointment time' };
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
        <p className="text-gray-600 mb-2">No appointments scheduled.</p>
        <p className="text-sm text-gray-500">
          Your confirmed and pending appointments will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {bookings.slice(0, 5).map((booking) => (
          <Card key={booking.id} className="p-3 sm:p-4">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                    <AvatarImage src={booking.mentee.avatar_url} />
                    <AvatarFallback>
                      {booking.mentee.first_name[0]}{booking.mentee.last_name[0]}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                      {booking.mentee.first_name} {booking.mentee.last_name}
                    </h3>
                    {booking.application_text_id && (
                      <p className="text-xs text-muted-foreground">App ID: {booking.application_text_id}</p>
                    )}
                    
                    {/* Date and time - stack on mobile */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        {format(parseISO(booking.booked_date), 'EEE, MMM d')}
                      </span>
                      {(() => {
                        // Convert UTC booking time to user's local timezone
                        const bookingLocal = convertFromUTC(
                          booking.booked_date,
                          booking.start_time,
                          userTimezone
                        );

                        const endLocal = convertFromUTC(
                          booking.booked_date,
                          booking.end_time,
                          userTimezone
                        );

                        return (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                            {bookingLocal.localTime} - {endLocal.localTime}
                            <span className="text-xs text-muted-foreground">
                              ({getTimezoneAbbreviation(userTimezone)})
                            </span>
                          </span>
                        );
                      })()}
                    </div>
                    
                    {/* Badges - better mobile layout */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge className={`${getCourseTypeColor(booking.course_type || '')} text-xs`}>
                        {getCourseTypeDisplay(booking.course_type || '', booking.subscription_type)}
                      </Badge>
                      <Badge className={`${getStatusColor(booking.status)} text-xs`}>
                        {booking.status}
                      </Badge>
                    </div>
                    
                    {booking.notes && (
                      <p className="text-xs sm:text-sm text-gray-600 mt-2 line-clamp-2">{booking.notes}</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {shouldShowActionButtons(booking) && (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {/* Start Call Button - only for confirmed voice/video calls */}
                    {booking.status === 'confirmed' && (courseTypes[booking.id] === 'Voice/Video Call' || courseTypes[booking.id] === 'Subscription') && (
                      <div className="flex items-center gap-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{getCallButtonState(booking).tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="default"
                          size="sm"
                          className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleStartCall(booking)}
                          disabled={!getCallButtonState(booking).enabled || callInProgress[booking.id] || isCreatingRoom}
                        >
                          <Phone className="h-3 w-3 mr-1" />
                          {callInProgress[booking.id] ? 'Starting...' : 'Start Call'}
                         </Button>
                       </div>
                     )}

                     {/* Message Mentee Button - only for subscriptions */}
                    {shouldShowMessageButton(booking.id) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={() => handleMessageMentee(booking)}
                      >
                        <MessageCircle className="h-3 w-3 mr-1" />
                        Message
                      </Button>
                    )}

                    {/* Confirm Button - only for pending bookings */}
                    {booking.status === 'pending' && (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleConfirm(booking)}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Confirm
                      </Button>
                    )}

                    {/* Request Change Button - only before session end time */}
                    {shouldShowRequestChange(booking) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={() => handleRequestChange(booking)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Request Change
                      </Button>
                    )}

                    {/* Mark Complete Button - only for confirmed voice/video calls after session end time */}
                    {shouldShowMarkComplete(booking) && (
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 px-3 text-xs bg-emu-blue hover:bg-emu-darkblue text-white"
                        onClick={() => setShowConfirmDialog(booking.id)}
                        disabled={isCompleting[booking.id]}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {isCompleting[booking.id] ? 'Completing...' : 'Mark Complete'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {bookings.length > 5 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" className="text-emu-blue">
              View All Appointments
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog for Mark Complete */}
      {showConfirmDialog && (
        <AlertDialog open={!!showConfirmDialog} onOpenChange={() => setShowConfirmDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark Appointment as Complete</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to mark this appointment as complete? This action cannot be undone and will allow both you and the mentee to leave reviews.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  const booking = bookings.find(b => b.id === showConfirmDialog);
                  if (booking) handleMarkAsComplete(booking);
                }}
                disabled={showConfirmDialog ? isCompleting[showConfirmDialog] : false}
              >
                {showConfirmDialog && isCompleting[showConfirmDialog] ? 'Completing...' : 'Mark Complete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Daily Call Manager for call duration tracking */}
      {isCallActive && (
        <DailyCallManager
          isCallActive={isCallActive}
          onCallEnd={endCallTimer}
        />
      )}
    </>
  );
};

export default UpcomingAppointments;
